import { useState } from "react";
import { Link } from "react-router-dom";
import type {
  AttentionLead,
  CycleBreakdownRow,
  FieldDistribution,
  LeadStage,
  ProductBreakdownRow,
  VisitMetrics,
} from "@school-sales-ai/shared";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import {
  useReportOverview,
  RANGE_LABELS,
  type RangeKey,
} from "@/hooks/useReportOverview";
import { PageHeader } from "@/components/PageHeader";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatElapsed } from "@/lib/format-elapsed";
import { STAGE_ORDER, STAGE_LABELS, STAGE_CLASSES } from "@/lib/stage-config";
import { usePipelineStagesQuery } from "@/hooks/usePipelineStages";
import { useClientFeatures } from "@/hooks/useClientFeatures";
import { useCatalogNoun } from "@/hooks/useCatalogNoun";
import { FEATURE_KEYS } from "@school-sales-ai/shared";

// ─── Shared card wrapper ─────────────────────────────────────────────────────

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }): React.JSX.Element {
  return (
    <div className={`bg-white border border-ink-100 rounded-[14px] shadow-sm ${className}`}>
      {children}
    </div>
  );
}

/**
 * A row label clipped to a fixed-width column, with the full text on hover.
 *
 * The demand and distribution rows keep their labels in a narrow column so every
 * bar starts at the same x — which cuts most real program names ("Diplomado en
 * Diseño de Interiores I Presencial" shows as "Diplomado en Di…"). Replaces the
 * native `title`, which took about a second to appear and rendered outside the
 * design. Radix tooltips do not open on touch, so the mobile layout still relies
 * on the label itself; that is the same as before.
 */
function TruncatedLabel({
  text,
  className,
}: {
  text: string;
  className: string;
}): React.JSX.Element {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <div className={className}>{text}</div>
      </TooltipTrigger>
      <TooltipContent side="top" align="start" className="max-w-[280px]">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="text-[11px] font-bold text-ink-500 uppercase tracking-[0.07em] mb-3">
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="text-[12px] text-ink-400 py-2">{children}</div>;
}

// ─── Range selector ──────────────────────────────────────────────────────────

const RANGE_KEYS: RangeKey[] = ["30d", "90d", "year", "all"];

function RangeSelector({
  value,
  onChange,
}: {
  value: RangeKey;
  onChange: (k: RangeKey) => void;
}): React.JSX.Element {
  return (
    <div className="inline-flex rounded-[10px] border border-ink-100 bg-white p-[3px] gap-[2px]">
      {RANGE_KEYS.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onChange(k)}
          className={`px-[10px] py-[5px] rounded-[7px] text-[12px] font-semibold transition-colors ${
            value === k ? "bg-moss text-white" : "text-ink-500 hover:text-ink-900"
          }`}
        >
          {RANGE_LABELS[k]}
        </button>
      ))}
    </div>
  );
}

// ─── KPI grid ────────────────────────────────────────────────────────────────

type KpiItem = { label: string; value: string | number; sub: string };

function KpiCard({ item }: { item: KpiItem }): React.JSX.Element {
  return (
    <Card className="p-[14px]">
      <div className="text-[10px] font-semibold text-ink-500 uppercase tracking-[0.06em] mb-1">
        {item.label}
      </div>
      <div className="font-display font-extrabold text-[34px] text-ink-900 leading-none">
        {item.value}
      </div>
      <div className="text-[10px] text-ink-400 mt-[3px]">{item.sub}</div>
    </Card>
  );
}

// ─── Stage funnel ────────────────────────────────────────────────────────────

// Rows come from the client's live columns, not the 7 seeded keys: a custom column
// used to vanish from this funnel entirely (`STAGE_ORDER.map` drops unknown keys),
// which also renormalized every percentage over a partial total.
function StageFunnel({
  byStage,
  labels,
}: {
  byStage: { stage: LeadStage; count: number }[];
  labels: Record<string, string>;
}): React.JSX.Element {
  const maxCount = Math.max(...byStage.map((s) => s.count), 1);
  const total = byStage.reduce((a, s) => a + s.count, 0);

  return (
    <Card className="p-4">
      <SectionTitle>Embudo por etapa</SectionTitle>
      <div className="flex flex-col gap-[9px]">
        {byStage.map(({ stage, count }) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const fillW = Math.round((count / maxCount) * 100);
          const stageClass = STAGE_CLASSES[stage] ?? "bg-ink-100 text-ink-600";
          const bgClass = stageClass.split(" ")[0] ?? "bg-ink-100";

          return (
            <div key={stage} className="flex items-center gap-2">
              <div className="w-[72px] flex-shrink-0 text-right text-[11px] font-medium text-ink-700">
                {labels[stage] ?? STAGE_LABELS[stage] ?? stage}
              </div>
              <div className="flex-1 h-[18px] bg-ink-050 rounded-[4px] overflow-hidden">
                <div
                  className={`h-full ${bgClass} rounded-[4px] flex items-center pl-[5px] transition-[width] duration-500`}
                  style={{ width: `${fillW}%` }}
                >
                  {count > 0 && (
                    <span className="text-[10px] font-semibold text-ink-700">
                      {count}
                    </span>
                  )}
                </div>
              </div>
              <div className="w-6 flex-shrink-0 text-right text-[10px] text-ink-400">
                {pct}%
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Demand by product / cycle ───────────────────────────────────────────────
// Single-series magnitude bars (leads per product/cycle) with a conversion chip.
// One brand hue — not a categorical palette — so no CVD concern.

type DemandRow = {
  key: string;
  name: string;
  total: number;
  converted: number;
  avgScore: number;
};

function DemandCard({
  title,
  rows,
}: {
  title: string;
  rows: DemandRow[];
}): React.JSX.Element {
  const maxTotal = Math.max(...rows.map((r) => r.total), 1);

  return (
    // Provider renders no DOM node, so the Card stays the grid child.
    <TooltipProvider>
      <Card className="p-4">
        <SectionTitle>{title}</SectionTitle>
        {rows.length === 0 ? (
          <EmptyHint>Sin datos en este periodo.</EmptyHint>
        ) : (
          <div className="flex flex-col gap-[10px]">
            {rows.map((r) => {
              const conv = r.total > 0 ? Math.round((r.converted / r.total) * 100) : 0;
              const fillW = Math.round((r.total / maxTotal) * 100);
              return (
                <div key={r.key} className="flex items-center gap-2">
                  <TruncatedLabel
                    text={r.name}
                    className="w-[96px] flex-shrink-0 truncate text-right text-[11px] font-medium text-ink-700"
                  />
                  <div className="flex-1 h-[18px] bg-ink-050 rounded-[4px] overflow-hidden">
                    <div
                      className="h-full bg-moss rounded-[4px] flex items-center pl-[6px] transition-[width] duration-500"
                      style={{ width: `${Math.max(fillW, r.total > 0 ? 8 : 0)}%` }}
                    >
                      {r.total > 0 && (
                        <span className="text-[10px] font-semibold text-white">{r.total}</span>
                      )}
                    </div>
                  </div>
                  <div className="w-[104px] flex-shrink-0 text-right text-[10px] leading-tight">
                    <span className="font-semibold text-moss">{conv}% insc.</span>
                    <span className="text-ink-400"> · ⌀{r.avgScore}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </TooltipProvider>
  );
}

// ─── Captured-field distribution ─────────────────────────────────────────────
// Per field, buckets are rendered as magnitude bars (labels outside the fill, so
// no text-on-fill contrast concern).

function FieldDistributionCard({
  fields,
}: {
  fields: FieldDistribution[];
}): React.JSX.Element {
  const withData = fields.filter((f) => f.buckets.length > 0);

  return (
    <TooltipProvider>
      <Card className="p-4">
        <SectionTitle>Distribución de campos a recabar</SectionTitle>
        {withData.length === 0 ? (
          <EmptyHint>Aún no hay respuestas capturadas para tus campos configurados.</EmptyHint>
        ) : (
          <div className="flex flex-col gap-4">
            {withData.map((field) => {
              const fieldTotal = field.buckets.reduce((a, b) => a + b.count, 0);
              const maxCount = Math.max(...field.buckets.map((b) => b.count), 1);
              return (
                <div key={field.name}>
                  <div className="text-[12px] font-semibold text-ink-900 mb-[6px]">
                    {field.label}
                  </div>
                  <div className="flex flex-col gap-[6px]">
                    {field.buckets.map((b) => {
                      const pct = fieldTotal > 0 ? Math.round((b.count / fieldTotal) * 100) : 0;
                      const fillW = Math.round((b.count / maxCount) * 100);
                      return (
                        <div key={b.value} className="flex items-center gap-2">
                          <TruncatedLabel
                            text={b.value}
                            className="w-[110px] flex-shrink-0 truncate text-right text-[11px] text-ink-700"
                          />
                          <div className="flex-1 h-[14px] bg-ink-050 rounded-[4px] overflow-hidden">
                            <div
                              className="h-full bg-brand-primary-light rounded-[4px] transition-[width] duration-500"
                              style={{ width: `${Math.max(fillW, b.count > 0 ? 4 : 0)}%` }}
                            />
                          </div>
                          <div className="w-[64px] flex-shrink-0 text-right text-[10px] text-ink-500">
                            <span className="font-semibold text-ink-700">{b.count}</span>
                            <span className="text-ink-400"> · {pct}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </TooltipProvider>
  );
}

// ─── Needs attention ─────────────────────────────────────────────────────────

const ATTENTION_META: Record<
  AttentionLead["reason"],
  { label: string; className: string }
> = {
  hot_unattended: { label: "Caliente sin atender", className: "bg-score-hot-bg text-score-hot-fg" },
  stalled: { label: "Estancado", className: "bg-score-warm-bg text-score-warm-fg" },
};

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / (60 * 60 * 1000));
  return `hace ${formatElapsed(hours)}`;
}

function NeedsAttentionCard({
  leads,
  noProductLabel,
}: {
  leads: AttentionLead[];
  noProductLabel: string;
}): React.JSX.Element {
  return (
    <Card className="p-4">
      <SectionTitle>Requieren atención</SectionTitle>
      {leads.length === 0 ? (
        <EmptyHint>Ningún lead requiere atención en este momento. ✓</EmptyHint>
      ) : (
        <div className="flex flex-col">
          {leads.map((lead, i) => {
            const meta = ATTENTION_META[lead.reason];
            return (
              <Link
                key={lead.id}
                to={`/leads/${lead.id}`}
                className={`flex items-center gap-3 py-[9px] hover:bg-ink-050 -mx-2 px-2 rounded-[6px] transition-colors ${
                  i < leads.length - 1 ? "border-b border-ink-050" : ""
                }`}
              >
                <ScoreBadge score={lead.score} size="xs" />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-ink-900 truncate">
                    {lead.name ?? "Sin nombre"}
                  </div>
                  <div className="text-[11px] text-ink-400 truncate">
                    {lead.productName ?? noProductLabel} · {timeAgo(lead.lastInteractionAt)}
                  </div>
                </div>
                <span
                  className={`flex-shrink-0 text-[10px] font-semibold px-2 py-[3px] rounded-full ${meta.className}`}
                >
                  {meta.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Channel donut ────────────────────────────────────────────────────────────

function describeArc(
  cx: number,
  cy: number,
  r: number,
  startAngle: number,
  endAngle: number,
): string {
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const x1 = cx + r * Math.cos(toRad(startAngle - 90));
  const y1 = cy + r * Math.sin(toRad(startAngle - 90));
  const x2 = cx + r * Math.cos(toRad(endAngle - 90));
  const y2 = cy + r * Math.sin(toRad(endAngle - 90));
  const large = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
}

type ChannelSegment = { label: string; count: number; color: string };

/**
 * Where the period's leads came from. Instagram is a segment only once it has a
 * lead, so a client without the channel sees the same two-way split as before.
 */
function ChannelDonut({
  webCount,
  waCount,
  igCount,
}: {
  webCount: number;
  waCount: number;
  igCount: number;
}): React.JSX.Element {
  const segments: ChannelSegment[] = [
    { label: "Web", count: webCount, color: "oklch(0.55 0.12 240)" },
    { label: "WhatsApp", count: waCount, color: "oklch(0.50 0.14 155)" },
    ...(igCount > 0 ? [{ label: "Instagram", count: igCount, color: "oklch(0.58 0.19 350)" }] : []),
  ];
  const total = segments.reduce((sum, seg) => sum + seg.count, 0);
  const pct = (count: number): number => (total > 0 ? Math.round((count / total) * 100) : 0);
  const visible = segments.filter((seg) => seg.count > 0);

  // Cumulative arcs. A single non-empty segment is a full circle: an arc from 0
  // to 360 degrees has coincident end points and draws nothing.
  let start = 0;
  const arcs = visible.map((seg) => {
    const end = start + (seg.count / Math.max(total, 1)) * 360;
    const arc = { ...seg, start, end };
    start = end;
    return arc;
  });

  return (
    <Card className="p-4">
      <SectionTitle>Canal de origen</SectionTitle>
      <div className="flex items-center gap-5">
        <svg width="76" height="76" viewBox="0 0 76 76" aria-hidden="true">
          {total === 0 ? (
            <circle cx="38" cy="38" r="28" fill="none" stroke="oklch(0.93 0.002 90)" strokeWidth="12" />
          ) : arcs.length === 1 && arcs[0] ? (
            <circle cx="38" cy="38" r="28" fill="none" stroke={arcs[0].color} strokeWidth="12" />
          ) : (
            arcs.map((arc) => (
              <path
                key={arc.label}
                d={describeArc(38, 38, 28, arc.start, arc.end)}
                fill="none"
                stroke={arc.color}
                strokeWidth="12"
                strokeLinecap="butt"
              />
            ))
          )}
          <circle cx="38" cy="38" r="22" fill="white" />
          <text x="38" y="42" textAnchor="middle" fontSize="13" fontWeight="700" fill="oklch(0.20 0.004 90)">
            {total}
          </text>
        </svg>

        <div className="flex flex-col gap-[10px]">
          {segments.map((ch) => (
            <div key={ch.label} className="flex items-center gap-2">
              <div
                className="w-[10px] h-[10px] rounded-[2px] flex-shrink-0"
                style={{ background: ch.color }}
              />
              <span className="text-[13px] text-ink-900">{ch.label}</span>
              <span className="text-[13px] font-semibold text-ink-900 ml-1">{ch.count}</span>
              <span className="text-[11px] text-ink-400">{pct(ch.count)}%</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ─── Bot ops ─────────────────────────────────────────────────────────────────

function BotOpsCard({
  botOnly,
  humanIntervention,
}: {
  botOnly: number;
  humanIntervention: number;
}): React.JSX.Element {
  const rows: { label: string; value: string }[] = [
    { label: "Solo bot", value: `${botOnly} leads` },
    { label: "Intervención humana", value: `${humanIntervention} leads` },
  ];

  return (
    <Card className="p-4">
      <SectionTitle>Operación del bot</SectionTitle>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`flex justify-between py-[10px] text-[13px] ${i < rows.length - 1 ? "border-b border-ink-050" : ""}`}
        >
          <span className="text-ink-700">{row.label}</span>
          <span className="font-semibold text-ink-900">{row.value}</span>
        </div>
      ))}
    </Card>
  );
}

// ─── Visit funnel ────────────────────────────────────────────────────────────

/**
 * Asked → booked → showed up, plus the manual queue.
 *
 * "Por coordinar" is the number this card exists for: with no advisor
 * availability loaded — a valid state while a school is being set up — every
 * request degrades to a human booking it by hand, and until now that queue was
 * invisible. It is shown as a highlighted stat, not buried in the breakdown.
 */
function VisitFunnelCard({ visits }: { visits: VisitMetrics }): React.JSX.Element {
  const steps = [
    { label: "Pidieron agendar", value: visits.requested },
    { label: "Agendadas", value: visits.booked },
    { label: "Asistieron", value: visits.attended },
  ];
  const max = Math.max(...steps.map((s) => s.value), 1);

  const breakdown: { label: string; value: number }[] = [
    { label: "Agendadas por el agente", value: visits.bookedByAgent },
    { label: "Agendadas desde el CRM", value: visits.bookedByCrm },
    { label: "No asistieron", value: visits.noShow },
    { label: "Canceladas", value: visits.cancelled },
  ];

  const hasData = visits.requested > 0 || visits.booked > 0;

  return (
    <Card className="p-4">
      <SectionTitle>Citas y visitas</SectionTitle>
      {!hasData ? (
        <EmptyHint>Todavía no hay peticiones de cita en este periodo.</EmptyHint>
      ) : (
        <>
          {steps.map((s) => (
            <div key={s.label} className="mb-2.5 last:mb-0">
              <div className="flex justify-between text-[12px] mb-1">
                <span className="text-ink-700">{s.label}</span>
                <span className="font-semibold text-ink-900">{s.value}</span>
              </div>
              <div className="h-[6px] rounded-full bg-ink-050 overflow-hidden">
                <div
                  className="h-full rounded-full bg-lime-deep"
                  style={{ width: `${(s.value / max) * 100}%` }}
                />
              </div>
            </div>
          ))}

          {visits.routedToHuman > 0 && (
            <div className="mt-3 flex items-center justify-between rounded-[10px] bg-ink-050 px-3 py-2">
              <span className="text-[12px] font-medium text-ink-700">
                Por coordinar a mano
              </span>
              <span className="font-display text-[18px] font-extrabold text-ink-900 leading-none">
                {visits.routedToHuman}
              </span>
            </div>
          )}

          <div className="mt-3 border-t border-ink-050 pt-2">
            {breakdown.map((b) => (
              <div key={b.label} className="flex justify-between py-[5px] text-[12px]">
                <span className="text-ink-500">{b.label}</span>
                <span className="text-ink-700">{b.value}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────────

function SkeletonReports(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-2.5">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-[88px] rounded-[14px] bg-ink-100 animate-pulse" />
        ))}
      </div>
      <div className="h-[200px] rounded-[14px] bg-ink-100 animate-pulse" />
      <div className="h-[160px] rounded-[14px] bg-ink-100 animate-pulse" />
      <div className="h-[180px] rounded-[14px] bg-ink-100 animate-pulse" />
      <div className="h-[160px] rounded-[14px] bg-ink-100 animate-pulse" />
      <div className="h-[200px] rounded-[14px] bg-ink-100 animate-pulse" />
    </div>
  );
}

// ─── Data shaping ────────────────────────────────────────────────────────────

function toProductRows(rows: ProductBreakdownRow[]): DemandRow[] {
  return rows.map((r) => ({
    key: r.productId ?? "none",
    name: r.name,
    total: r.total,
    converted: r.converted,
    avgScore: r.avgScore,
  }));
}

function toCycleRows(rows: CycleBreakdownRow[]): DemandRow[] {
  return rows.map((r) => ({
    key: r.cycleId ?? "none",
    name: r.label,
    total: r.total,
    converted: r.converted,
    avgScore: r.avgScore,
  }));
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function Reports(): React.JSX.Element {
  const [range, setRange] = useState<RangeKey>("30d");
  const { data, isLoading } = useReportOverview(range);
  const { data: stages } = usePipelineStagesQuery();
  const { data: features } = useClientFeatures();
  const noun = useCatalogNoun();

  const stageCount = (s: LeadStage): number =>
    data?.byStage.find((x) => x.stage === s)?.count ?? 0;

  const total = data?.total ?? 0;
  const avgScore = data?.avgScore ?? 0;
  const hotLeads = data?.hotLeads ?? 0;
  // The conversion column is whatever the client designated, resolved server-side.
  // Reading the literal key `enrolled` is what made this tile show the pilot's
  // "Poco calificado" count under the label "Inscritos".
  const conversionKey = data?.conversionStageKey ?? null;
  const converted = conversionKey ? stageCount(conversionKey) : 0;
  const webCount = data?.byChannel.web ?? 0;
  const waCount = data?.byChannel.whatsapp ?? 0;
  // `?? 0` also covers a backend one deploy behind, which sends no `instagram`.
  const igCount = data?.byChannel.instagram ?? 0;
  const botOnly = data?.botOps.botOnly ?? 0;
  const humanIntervention = data?.botOps.humanIntervention ?? 0;

  const kpis: KpiItem[] = [
    { label: "Total leads", value: total, sub: "En el periodo" },
    { label: "Score promedio", value: avgScore, sub: "Escala 0–100" },
    { label: "Leads calientes", value: hotLeads, sub: "Score ≥ 71" },
    {
      label: data?.conversionStageLabel ?? "Inscritos",
      value: converted,
      sub: conversionKey ? "Conversión final" : "Sin columna de cierre",
    },
  ];

  // `STAGE_ORDER` is only the pre-load fallback; the real order is the client's own.
  const orderedKeys = stages?.map((s) => s.key) ?? STAGE_ORDER;
  const stageLabels = Object.fromEntries((stages ?? []).map((s) => [s.key, s.label]));
  const byStage = orderedKeys.map((s) => ({ stage: s, count: stageCount(s) }));
  const productRows = toProductRows(data?.byProduct ?? []);
  const cycleRows = toCycleRows(data?.byCycle ?? []);
  // Demand per intake period belongs to the enrollment capability; demand per
  // product is industry-blind and stays for everyone. The server skips the
  // aggregation for an unentitled client, so `byCycle` is already empty — this
  // hides the card rather than rendering an empty one that reads as "no demand".
  const canEnrollment = (features ?? []).includes(FEATURE_KEYS.enrollment);
  const fields = data?.fields ?? [];
  const needsAttention = data?.needsAttention ?? [];
  const visits: VisitMetrics = data?.visits ?? {
    requested: 0,
    booked: 0,
    bookedByAgent: 0,
    bookedByCrm: 0,
    routedToHuman: 0,
    attended: 0,
    noShow: 0,
    cancelled: 0,
  };

  const content = isLoading ? (
    <SkeletonReports />
  ) : (
    <>
      <div className="grid grid-cols-2 gap-2.5">
        {kpis.map((k) => (
          <KpiCard key={k.label} item={k} />
        ))}
      </div>
      <StageFunnel byStage={byStage} labels={stageLabels} />
      <NeedsAttentionCard leads={needsAttention} noProductLabel={`Sin ${noun.singular}`} />
      <VisitFunnelCard visits={visits} />
      <DemandCard title={`Demanda por ${noun.singular}`} rows={productRows} />
      {canEnrollment && <DemandCard title="Demanda por ciclo" rows={cycleRows} />}
      <FieldDistributionCard fields={fields} />
      <ChannelDonut webCount={webCount} waCount={waCount} igCount={igCount} />
      <BotOpsCard botOnly={botOnly} humanIntervention={humanIntervention} />
    </>
  );

  return (
    <>
      {/* ── Mobile ────────────────────────────────── */}
      <div className="md:hidden flex flex-col h-full">
        <PageHeader title="Reportes" />
        <div className="px-4 pt-3">
          <RangeSelector value={range} onChange={setRange} />
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ink-300">
          {content}
        </div>
      </div>

      {/* ── Desktop ───────────────────────────────── */}
      <div className="hidden md:flex flex-col gap-6 p-6 overflow-y-auto h-full">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-bold text-ink-900">Reportes</h1>
          <RangeSelector value={range} onChange={setRange} />
        </div>
        {isLoading ? (
          <SkeletonReports />
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4">
              {kpis.map((k) => (
                <KpiCard key={k.label} item={k} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <StageFunnel byStage={byStage} labels={stageLabels} />
              <NeedsAttentionCard leads={needsAttention} noProductLabel={`Sin ${noun.singular}`} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <DemandCard title={`Demanda por ${noun.singular}`} rows={productRows} />
              {canEnrollment && <DemandCard title="Demanda por ciclo" rows={cycleRows} />}
            </div>
            <VisitFunnelCard visits={visits} />
            <FieldDistributionCard fields={fields} />
            <div className="grid grid-cols-2 gap-4">
              <ChannelDonut webCount={webCount} waCount={waCount} igCount={igCount} />
              <BotOpsCard botOnly={botOnly} humanIntervention={humanIntervention} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
